import React, { Suspense, lazy } from "react";
import ScrollToTop from "./ScrollToTop";
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import Layout from "./Layout";
import MenuFull from "../components/MenuFull";
import { useModal } from "../contexts/ModalContext";
import InquiryModal from "../components/InquiryModal";

// Home is kept as a direct import (critical path - LCP page)
import Home from "../pages/home/Home";

const reloadKey = "bigbin-route-reload";

function isChunkLoadError(error) {
  const message = error?.message || "";
  const name = error?.name || "";

  return (
    name === "ChunkLoadError" ||
    message.includes("Loading chunk") ||
    message.includes("Failed to fetch dynamically imported module") ||
    message.includes("Importing a module script failed")
  );
}

function lazyWithRetry(importer) {
  return lazy(() =>
    importer()
      .then((module) => {
        if (typeof window !== "undefined") {
          sessionStorage.removeItem(reloadKey);
        }
        return module;
      })
      .catch((error) => {
        if (
          typeof window !== "undefined" &&
          isChunkLoadError(error) &&
          sessionStorage.getItem(reloadKey) !== window.location.pathname
        ) {
          sessionStorage.setItem(reloadKey, window.location.pathname);
          window.location.reload();
          return new Promise(() => {});
        }

        throw error;
      })
  );
}

const routeImports = [
  () => import("../pages/contactUs/ContactUs"),
  () => import("../pages/HowWorks/HowItWorks"),
  () => import("../pages/servicesPages/Services/Service"),
  () => import("../pages/blog/Blog"),
  () => import("../pages/blogDetails/BlogDetails"),
  () => import("../pages/about/About"),
  () => import("../pages/servicesPages/OtherService/OtherService"),
  () => import("../pages/prohibited/ProhibitedItems"),
  () => import("../pages/pricingPage/Pricing"),
  () => import("../pages/thankYou/ThankYou"),
  () => import("../pages/error/ErrorPage"),
  () => import("../pages/privacyPolicy/PrivacyPolicy"),
  () => import("../pages/termsCondition/TermsCondition"),
  () => import("../pages/faq/Faq"),
  () => import("../pages/servicesPages/ConstructionService.js/ConstructionServices"),
  () => import("../pages/servicesPages/CommercialServices.js/CommercialServices"),
  () => import("../pages/LandingPage/DumpsterEdmonton/DumpsterEdmonton"),
];

class RouteErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error) {
    if (
      typeof window !== "undefined" &&
      isChunkLoadError(error) &&
      sessionStorage.getItem(reloadKey) !== window.location.pathname
    ) {
      sessionStorage.setItem(reloadKey, window.location.pathname);
      window.location.reload();
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.locationKey !== this.props.locationKey && this.state.hasError) {
      this.setState({ hasError: false, error: null });
    }
  }

  render() {
    if (this.state.hasError) {
      return <PageLoader />;
    }

    return this.props.children;
  }
}

// All other routes lazy-loaded for code splitting
const ContactUs = lazyWithRetry(routeImports[0]);
const HowItWorks = lazyWithRetry(routeImports[1]);
const Service = lazyWithRetry(routeImports[2]);
const Blog = lazyWithRetry(routeImports[3]);
const BlogDetails = lazyWithRetry(routeImports[4]);
const About = lazyWithRetry(routeImports[5]);
const OtherService = lazyWithRetry(routeImports[6]);
const ProhibitedItems = lazyWithRetry(routeImports[7]);
const Pricing = lazyWithRetry(routeImports[8]);
const ThankYou = lazyWithRetry(routeImports[9]);
const ErrorPage = lazyWithRetry(routeImports[10]);
const PrivacyPolicy = lazyWithRetry(routeImports[11]);
const TermsCondition = lazyWithRetry(routeImports[12]);
const Faq = lazyWithRetry(routeImports[13]);
const ConstructionServices = lazyWithRetry(routeImports[14]);
const CommercialServices = lazyWithRetry(routeImports[15]);
const DumpsterEdmonton = lazyWithRetry(routeImports[16]);

function Redirect({ to }) {
  if (typeof window !== "undefined") {
    return <Navigate to={to} replace />;
  }
  return null;
}

// Minimal fallback - no spinner to avoid CLS
function PageLoader() {
  return <div style={{ minHeight: '60vh' }} />;
}

export default function NavRouter({ initialData }) {
  const { modalShow, closeQueryModal } = useModal();
  const location = useLocation();

  React.useEffect(() => {
    const preloadRoutes = () => {
      routeImports.forEach((preload) => {
        preload().catch(() => {});
      });
    };

    if ("requestIdleCallback" in window) {
      const idleId = window.requestIdleCallback(preloadRoutes, { timeout: 3000 });
      return () => window.cancelIdleCallback?.(idleId);
    }

    const timeoutId = setTimeout(preloadRoutes, 1200);
    return () => clearTimeout(timeoutId);
  }, []);

  return (
    <>
      <ScrollToTop />
      <MenuFull />
      <InquiryModal show={modalShow} setShow={closeQueryModal} />
      <RouteErrorBoundary locationKey={location.pathname}>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<Layout />}>
              <Route index element={<Home initialData={initialData} />} />
              <Route path="contact-us/" element={<ContactUs />} />
              <Route path="dumpster-rental-services-in-edmonton/" element={<DumpsterEdmonton />} />
              <Route path="how-to-book-a-bin/" element={<HowItWorks />} />
              <Route path="residential-dumpsters-edmonton/" element={<Service />} />
              <Route path="other-services/" element={<OtherService />} />
              <Route path="construction-dumpsters-edmonton/" element={<ConstructionServices />} />
              <Route path="commercial-dumpster-edmonton/" element={<CommercialServices />} />
              {/* <Route path="general-dumpster-edmonton/" element={<DumpsterServices />} /> */}
              <Route path="prohibited-items/" element={<ProhibitedItems />} />
              <Route path="thank-you" element={<ThankYou />} />
              <Route path="pricing/" element={<Pricing />} />
              <Route path="blogs/" element={<Blog initialData={initialData} />} />
              <Route path="blogs/:slug" element={<BlogDetails initialData={initialData} />} />
              <Route path="about-us/" element={<About />} />
              <Route path="faq/" element={<Faq />} />
              <Route path="privacy-policy/" element={<PrivacyPolicy />} />
              <Route path="return-and-refund-policy/" element={<TermsCondition />} />
              <Route path='/404' element={<ErrorPage />} />
              <Route path="*" element={<Redirect to="/404" />} />
            </Route>
          </Routes>
        </Suspense>
      </RouteErrorBoundary>
    </>
  )
}
